import React, { useState, useEffect, useContext, useRef } from "react";
import {
	fetchList,
	getElasticFacets,
	fetchAs,
	extractLinkData,
	getDateTimeFromValue,
} from "gatsby-frontend";

import { SiteContext } from "gatsby-frontend/src/Site";
import { SingleContext } from "gatsby-frontend/src/Page";
import {
	CircularProgress,
	Box,
	Alert,
	Grid as GridMaterial,
	Paper,
	TextField,
	Checkbox,
	Autocomplete,
	Pagination,
	IconButton,
	InputAdornment,
	Button,
	NativeSelect,
	FormControl,
	Typography,
} from "@mui/material";

import DatePicker from "react-datepicker";
import { addDays } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

import ClearIcon from "@mui/icons-material/Clear";

import SouthIcon from "@mui/icons-material/South";
import NorthIcon from "@mui/icons-material/North";
import { styled } from "@mui/material/styles";
import { Excerpt, Buttons } from "./../";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ListTable from "./ListTable";
import CalendarToggle from "./CalendarToggle";
import CalendarGrid from "./CalendarGrid";
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const FilterPaper = styled(Paper)(({ theme, layout }) => {
	return {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(1),
		backgroundColor: theme.palette.grey["100"],
		display: layout === "topbar" ? "flex" : "block",
		flexWrap: "wrap",
	};
});

const Grid = styled(GridMaterial)(({ theme, item }) => {
	if (item)
		return {
			width: "100%",
		};
});

function ListComponent(props) {
	const {
		id,
		mode, // grid, list, table, calendar
		types, //force elastic filter to include only these types
		typeNames = [],
		uuids, //force elastic filter to include only entities with these identifiers
		showFilters,
		useElastic = false,
		cols = 3,
		layout = "sidebar",
		isSidebar = false,
		moreLink,
		sx = {},
		field_table_columns,
		field_entities_list_by_terms,
		field_entities_list_by_content,
		field_entities_list_sort_by,
		field_entities_list_sort_order,
		global,
		hasDateRange,
		dateFilter = null, // past, future, null
		datePosition = "after",
	} = props;

	/**
	 * grab neccesary site and single context vars to perform fetchList
	 */
	const {
		isAuthenticated,
		dispatch,
		location,
		typeDefinitions,
		isPrivate,
		privateRoute,
		sortOptions,
		configs,
		themeConfig,
		pageData,
	} = useContext(SiteContext);

	const filtersTitle = themeConfig?.accessibilityOptions?.filtersTitle || null;
	const hideFilterByType = themeConfig?.globalSearch?.hideFilterByType || false;
	const showAllTermFilters =
		themeConfig?.globalSearch?.showAllTermFilters || false;

	let _sortOptions = [...sortOptions];

	const availableSortOptions = [
		{
			name: "Date Created",
			value: "created",
			direction: field_entities_list_sort_order || "desc",
		},
		{
			name: "Date Updated",
			value: "changed",
			direction: field_entities_list_sort_order || "desc",
		},
		{
			name: "Date",
			value: "field_start_date",
			direction: field_entities_list_sort_order || "desc",
		},
		{
			name: "Alphabetically",
			value: "alphabetically",
			direction: field_entities_list_sort_order || "asc",
		},
	];

	let defaultSortOption;

	let includedDefaultSortOption = sortOptions.find(
		(option) => option.value === field_entities_list_sort_by
	);

	if (includedDefaultSortOption) {
		defaultSortOption = { ...includedDefaultSortOption };
		if (field_entities_list_sort_order) {
			defaultSortOption.direction = field_entities_list_sort_order;
		}
	} else {
		defaultSortOption = availableSortOptions.find(
			(option) => option.value === field_entities_list_sort_by
		);
		if (defaultSortOption) {
			_sortOptions.unshift(defaultSortOption);
		}
	}

	if (_sortOptions && _sortOptions.length > 0 && !defaultSortOption) {
		defaultSortOption = _sortOptions.find(
			(option) => option.value === "_score"
		);
	}

	const singleContext = useContext(SingleContext);
	const { lists, includes, relationships } = singleContext;

	const preloaded = lists && typeof lists[id] !== "undefined";
	const searchPath = isPrivate ? `${privateRoute}/search` : "/search";

	const [filtersData, setFiltersData] = useState([]);
	// false state = list, true state = calendar
	let [calendarmode, setCalendarmode] = useState(false);
	let calendarCustomOption = themeConfig?.customOptions?.calendarDefault;
	useEffect(() => {
		if (calendarCustomOption !== undefined) {
			setCalendarmode(calendarCustomOption);
		}
	}, [themeConfig]);

	const [schemaFields, setSchemaFields] = useState([]);
	const [fullFacetsData, setFullFacetsData] = useState({});

	const [elasticConfigDone, setElasticConfigDone] = useState(false);

	const defaultSearchParameters = {
		searchQuery: "",
		searchFilters: {},
		searchSort: _sortOptions[0],
		localPagination: 1,
		dateFrom: null,
		dateTo: null,
		pageSize: props.limit || 20,
	};

	const [searchParameters, setSearchParameters] = useState(
		defaultSearchParameters
	);

	const [dateRangeLimits, setDateRangeLimits] = useState(null);

	const [searchPages, setSearchPages] = useState(
		preloaded ? lists[id].page : null
	);
	const [pagerTouched, setPagerTouched] = useState(!preloaded);
	const prevSearchTerm = useRef("");

	const filteringByContent =
		preloaded && lists[id].filteringByContent
			? lists[id].filteringByContent
			: null;

	const filteringByTerms =
		preloaded && lists[id].filteringByTerms ? lists[id].filteringByTerms : null;

	let hiddenFacets = field_entities_list_by_terms
		? field_entities_list_by_terms.map((f) => `field_${f.type.split("--")[1]}`)
		: [];
	if (field_entities_list_by_content) {
		hiddenFacets = [
			...hiddenFacets,
			...field_entities_list_by_content.map(
				(f) => `field_${f.type.split("--")[1]}`
			),
		];
	}
	hiddenFacets = [...new Set(hiddenFacets)];

	const excerptAttrs = {
		datePosition,
	};

	if (global) {
		excerptAttrs.global = true;
		excerptAttrs.thumbnailAlign = "right";
		excerptAttrs.thumbnailSize = themeConfig?.globalSearch?.thumbnailSize
			? themeConfig.globalSearch.thumbnailSize
			: "sm";
		excerptAttrs.orientation = themeConfig?.globalSearch?.thumbnailOrientation
			? themeConfig.globalSearch.thumbnailOrientation
			: "landscape";
		excerptAttrs.flush = themeConfig?.globalSearch?.flush
			? themeConfig.globalSearch.flush
			: true;
		excerptAttrs.thumbOption = themeConfig?.globalSearch?.thumbOption
			? themeConfig.globalSearch.thumbOption
			: "default";
	} else {
		if (props.thumbnailAlign)
			excerptAttrs.thumbnailAlign = props.thumbnailAlign;
		if (props.thumbnailSize) excerptAttrs.thumbnailSize = props.thumbnailSize;
	}

	const scrollToTop = () => {
		if (typeof window !== "undefined") {
			let single_body = document.getElementById("single-body");
			if (single_body) {
				let top_offset = single_body.offsetTop;
				window.scrollTo({
					top: top_offset,
					behavior: "smooth",
				});
			}
		}
	};

	const handlePageSizeChange = (event) => {
		setPagerTouched(true);
		let _searchParameters = {
			...searchParameters,
			pageSize: event.target.value,
			localPagination: 1,
		};
		setSearchParameters(_searchParameters);
		scrollToTop();
	};

	/**
	 * First render will fetchList from Drupal,
	 * then if a search query happens, items will be set from elastic response
	 */

	const [items, setItems] = useState(preloaded ? lists[id].items : null);
	const [loadingItems, setLoadingItems] = useState(!preloaded);

	useEffect(() => {
		if (showFilters || useElastic) {
			(async () => {
				let _dispatch = isAuthenticated ? dispatch : null;
				let endpoint = `elastic_search/?_format=json&query=GET_ALL_FACETS&private=${
					singleContext.private ? 1 : 0
				}&page_size=1${
					hasDateRange || dateFilter ? "&has_date_range=1" : ""
				}${getFixedFacetsParam()}`;

				let response = await fetchAs({
					endpoint,
					dispatch: _dispatch,
					testing: true,
				});

				if (response && response.data) {
					if (showAllTermFilters) {
						setFiltersData(getElasticFacets(response.data, typeDefinitions, hideFilterByType));
					}
					if (response.data.schema_fields) {
						setSchemaFields(response.data.schema_fields);
					}
					let max_date_str;
					let min_date_str;
					if (response.data.max_date && response.data.min_date) {
						max_date_str = response.data.max_date.split("T")[0] + `T23:59:59`;
						min_date_str = response.data.min_date.split("T")[0] + `T00:00:00`;
						setDateRangeLimits({
							max: new Date(max_date_str),
							min: new Date(min_date_str),
						});
					}
					if (response.data.facets) {
						let _all_facets = {};
						for (const [facet_name, facet_val] of Object.entries(
							response.data.facets
						)) {
							if (facet_name === "type") {
								_all_facets[facet_name] = facet_val[0].data.map((f) => {
									let typeDef = typeDefinitions.find(
										(t) => `node--${t.bundle}` === f.value
									);
									return {
										title: typeDef ? typeDef.name : f.value,
										value: f.value,
									};
								});
							} else {
								_all_facets[facet_name] = facet_val[0].data.map((f) => ({
									title: f.value,
									value: f.value,
								}));
							}
						}
						setFullFacetsData(_all_facets);
					}
				}
				setElasticConfigDone(true);
			})();
		}
	}, []);

	useEffect(() => {
		setQueryStringSearch();
	}, [location]);

	useEffect(() => {
		if (typeof window !== "undefined") {
			window.addEventListener("popstate", handlePopState);
		}
		return () => {
			if (typeof window !== "undefined") {
				window.removeEventListener("popstate", handlePopState);
			}
		};
	}, []);

	useEffect(() => {
		let fetchFromElastic =
			!!(showFilters || searchParameters.searchQuery) || useElastic;
		let fetchFromDrupal =
			!preloaded || (preloaded && searchPages && pagerTouched);
		let needToLoad = fetchFromElastic || fetchFromDrupal;
		setLoadingItems(needToLoad);
		const delaySearch = setTimeout(() => {
			if (fetchFromElastic && elasticConfigDone) {
				getElasticSearch();
				setUrlParams();
			} else if (fetchFromDrupal) {
				fetchDrupalContent(searchParameters);
			} else if (preloaded) {
				setItems(lists[id].items);
				setLoadingItems(false);
			}
			if (prevSearchTerm.current != searchParameters.searchQuery) {
				prevSearchTerm.current = searchParameters.searchQuery;
			}
		}, 500);

		return () => clearTimeout(delaySearch);
	}, [searchParameters, elasticConfigDone]);

	const handlePopState = (event) => {
		if (event.state && event.state.data) {
			let _searchParameters = { ...searchParameters, ...event.state.data };
			setSearchParameters(_searchParameters);
		}
	};

	const setQueryStringSearch = () => {
		const search = location.search;
		const params = new URLSearchParams(search);
		let querySearch = params.get("query");
		let filters = params.get("filter");
		let sort = params.get("sort");
		let _dateFrom = params.get("dateFrom");
		let _dateTo = params.get("dateTo");
		let page = params.get("page");
		let limit = params.get("limit");

		let _searchParameters = { ...searchParameters };

		if (querySearch) {
			_searchParameters.searchQuery = querySearch;
			if (!sort) {
				_searchParameters.searchSort = defaultSortOption;
			}
		}

		if (sort) {
			let _sortSetting = _sortOptions.find(
				(option) => option.value === sort.split("|")[1]
			);
			if (_sortSetting) {
				_searchParameters.searchSort = {
					name: _sortSetting.name,
					value: sort.split("|")[1],
					direction: sort.split("|")[0],
				};
			}
		}

		if (_dateFrom) {
			_searchParameters.dateFrom = _dateFrom;
		}

		if (_dateTo) {
			_searchParameters.dateTo = _dateTo;
		}

		if (filters) {
			filters = decodeURIComponent(filters);
			filters = filters.split("|");
			let values = {};
			filters.forEach((filterName) => {
				let query_filter = params.get(filterName);
				if (query_filter) {
					if (filterName === "type") {
						let vals = query_filter.split("|");
						values[filterName] = vals.map((v) => {
							let typeDef = typeDefinitions.find(
								(t) => `node--${t.bundle}` === v
							);
							return {
								label: typeDef ? typeDef.name : v,
								value: v,
							};
						});
					} else {
						let vals = query_filter.split("|");
						values[filterName] = vals.map((v) => ({ label: v, value: v }));
					}
				}
			});

			_searchParameters.searchFilters = {
				...searchParameters.searchFilters,
				...values,
			};
		}

		if (page) {
			_searchParameters.localPagination = page;
		}

		if (limit) {
			_searchParameters.pageSize = limit;
		}
		setSearchParameters(_searchParameters);
	};

	const fetchDrupalContent = async (_searchParameters) => {
		let _dispatch = isAuthenticated ? dispatch : null;

		let _excerpts = await fetchList({
			list_data: props,
			configs,
			includes,
			dispatch: _dispatch,
			relationships,
			paged: _searchParameters.localPagination,
			limit: _searchParameters.pageSize,
		});

		if (_excerpts.items.length > 0) {
			setItems(_excerpts.items);
		} else {
			setItems([]);
		}
		if (_excerpts.page) {
			setSearchPages(_excerpts.page);
		} else {
			setSearchPages(null);
		}
		setFiltersData([]);
		setLoadingItems(false);
	};

	const onChangeQuery = (e) => {
		e.preventDefault();
		let _searchParameters = {
			...searchParameters,
			searchQuery: e.target.value,
			searchSort: defaultSortOption,
		};
		setSearchParameters(_searchParameters);
	};

	const onChangeDateTo = (date) => {
		let _searchParameters = {
			...searchParameters,
			dateTo: date ? date : null,
		};
		setSearchParameters(_searchParameters);
	};

	const onChangeDateFrom = (date) => {
		let _searchParameters = {
			...searchParameters,
			dateFrom: date ? date : null,
		};
		setSearchParameters(_searchParameters);
	};

	const setUrlParams = () => {
		let params = [];
		let newState = { data: {} };
		if (searchParameters.dateFrom) {
			params.push(
				`date_from=${searchParameters.dateFrom.toLocaleDateString("en-US")}`
			);
			newState.data.dateFrom = searchParameters.dateFrom;
		}
		if (searchParameters.dateTo) {
			params.push(
				`date_to=${searchParameters.dateTo.toLocaleDateString("en-US")}`
			);
			newState.data.dateTo = searchParameters.dateTo;
		}
		if (
			searchParameters.searchQuery &&
			searchParameters.searchQuery.length > 0
		) {
			params.push(`query=${searchParameters.searchQuery}`);
			newState.data.searchQuery = searchParameters.searchQuery;
		}
		if (searchParameters.searchSort) {
			if (
				searchParameters.searchSort.direction !== defaultSortOption.direction ||
				searchParameters.searchSort.value !== defaultSortOption.value
			) {
				params.push(
					`sort=${searchParameters.searchSort.direction}|${searchParameters.searchSort.value}`
				);
				newState.data.searchSort = searchParameters.searchSort;
			}
		}
		if (
			searchParameters.searchFilters &&
			Object.keys(searchParameters.searchFilters).length > 0
		) {
			params.push(
				`filter=${encodeURIComponent(
					Object.keys(searchParameters.searchFilters).join("|")
				)}`
			);
			newState.data.searchFilters = searchParameters.searchFilters;
			for (const [filterName, filterValues] of Object.entries(
				searchParameters.searchFilters
			)) {
				const values = filterValues.map((op) => op.value).join("|");
				params.push(`${filterName}=${values}`);
			}
		}

		if (
			searchParameters.localPagination &&
			searchParameters.localPagination > 1
		) {
			params.push(`page=${searchParameters.localPagination}`);
			newState.data.localPagination = searchParameters.localPagination;
		}

		if (searchParameters.pageSize) {
			params.push(`limit=${searchParameters.pageSize}`);
			newState.data.pageSize = searchParameters.pageSize;
		}

		const current_page_url =
			typeof window !== "undefined"
				? `${window.location.origin}${window.location.pathname}`
				: "";

		if (params.length > 0) {
			let url = `${current_page_url}?${encodeURI(params.join("&"))}`;
			history.pushState(newState, "", url);
		} else {
			history.pushState(newState, "", current_page_url);
		}
	};

	const onChangeFilter = (e, value, id) => {
		if (e) {
			e.preventDefault();
		}
		const newFilters = { ...searchParameters.searchFilters };
		if (value) {
			if (value.length === 0) {
				delete newFilters[id];
			} else {
				newFilters[id] = value;
			}
		}
		let _searchParameters = {
			...searchParameters,
			searchFilters: newFilters,
			localPagination: 1,
		};
		setSearchParameters(_searchParameters);
	};

	const onClearFilters = (e) => {
		e.preventDefault();
		let _searchParameters = {
			...searchParameters,
			searchSort: _sortOptions[0],
			searchFilters: {},
			dateFrom: null,
			dateTo: null,
		};
		setSearchParameters(_searchParameters);
	};

	const onResetSearch = (e) => {
		e.preventDefault();
		let _searchParameters = {
			...searchParameters,
			searchQuery: "",
			searchSort: _sortOptions[0],
			searchFilters: {},
			dateFrom: null,
			dateTo: null,
		};
		setSearchParameters(_searchParameters);
	};

	const onClearSearch = () => {
		let _searchParameters = {
			...searchParameters,
			searchQuery: "",
			searchSort: _sortOptions[0],
		};
		setSearchParameters(_searchParameters);
	};

	const onChangeSort = ({ value, direction }) => {
		let selection = _sortOptions.find((item) => item.value == value);
		if (selection) {
			let _searchParameters = {
				...searchParameters,
				searchSort: { ...selection, direction },
				localPagination: 1,
			};
			setSearchParameters(_searchParameters);
		}
	};

	const onChangePage = (e, page) => {
		setPagerTouched(true);
		let _searchParameters = { ...searchParameters, localPagination: page };
		setSearchParameters(_searchParameters);
		scrollToTop();
	};

	const getFixedFacetsParam = () => {
		let byterm_constraints =
			field_entities_list_by_terms && filteringByTerms
				? field_entities_list_by_terms.map((a_term_rel) => {
						let term_name =
							filteringByTerms[a_term_rel.id] &&
							filteringByTerms[a_term_rel.id].attributes
								? filteringByTerms[a_term_rel.id].attributes.name
								: null;
						let a_term_def = {
							bundle: a_term_rel.type.split("--")[1],
							name: term_name,
						};
						return a_term_def;
				  })
				: [];

		let by_content_constraints =
			field_entities_list_by_content && filteringByContent
				? field_entities_list_by_content.map((a_content_rel) => {
						let entity_title =
							filteringByContent[a_content_rel.id] &&
							filteringByContent[a_content_rel.id].attributes
								? filteringByContent[a_content_rel.id].attributes.title
								: null;
						let a_content_def = {
							bundle: a_content_rel.type.split("--")[1],
							name: entity_title,
						};
						return a_content_def;
				  })
				: [];

		let fixed_facets = {};
		for (const a_rel of [...byterm_constraints, ...by_content_constraints]) {
			if (a_rel.name) {
				if (typeof fixed_facets[`field_${a_rel.bundle}`] === "undefined") {
					fixed_facets[`field_${a_rel.bundle}`] = [];
				}
				fixed_facets[`field_${a_rel.bundle}`].push(a_rel.name);
			}
		}
		let fixed_facets_arr = [];
		for (const [key, value] of Object.entries(fixed_facets)) {
			fixed_facets_arr.push({ [key]: value });
		}

		let query_string = "";
		if (fixed_facets_arr.length > 0) {
			query_string = `&fixed_facets=${encodeURIComponent(
				JSON.stringify(fixed_facets_arr)
			)}`;
		}

		if (typeNames.length > 0) {
			query_string += `&types=${typeNames.join("|")}`;
		}

		return query_string;
	};

	const getElasticSearch = async () => {
		let _dispatch = isAuthenticated ? dispatch : null;
		let filters = [];
		let filterValues = "";
		Object.keys(searchParameters.searchFilters).forEach((key) => {
			filters = [...filters, key];
			filterValues += `&${key}=${searchParameters.searchFilters[key]
				.map((item) => encodeURIComponent(item.value))
				.join("|")}`;
		});

		let endpoint = `elastic_search/?_format=json&query=${
			searchParameters.searchQuery
		}&private=${singleContext.private ? 1 : 0}&page_size=${
			searchParameters.pageSize
		}`;

		if (filters.length > 0) {
			endpoint += `&filter=${filters.join("|")}`;
			endpoint += filterValues;
		}

		endpoint += getFixedFacetsParam();

		let extra_attributes = ["field_product_new_badge", "field_subtitle"];

		if (typeNames.length > 0) {
			let typesWithAttrs = typeNames.filter(
				(type) => !!configs[type]?.options?.list?.attributes
			);
			if (typesWithAttrs.length > 0) {
				const attributesFromTypes = typesWithAttrs.reduce((acc, type) => {
					return [
						...acc,
						...Object.keys(configs[type].options.list.attributes),
					];
				}, []);
				extra_attributes = [...extra_attributes, ...attributesFromTypes];
			}
		}

		if (extra_attributes.length > 0) {
			endpoint += `&extra_attributes=${extra_attributes.join("|")}`;
		}

		if (uuids && uuids.length > 0) {
			endpoint += `&uuids=${uuids.join("|")}`;
		}

		if (schemaFields) {
			endpoint += `&schema_fields=${schemaFields.join("|")}`;
		}

		if (searchParameters.localPagination) {
			endpoint += `&current_page=${searchParameters.localPagination}`;
		}

		if (searchParameters.searchSort.value) {
			let sort_value = searchParameters.searchSort.value;
			if (typeNames && typeNames.length > 0) {
				let type_config = configs ? configs[typeNames[0]] : null;
				if (
					type_config &&
					type_config.sortBy &&
					type_config.sortBy[searchParameters.searchSort.value]
				) {
					sort_value += `.${type_config.sortBy[
						searchParameters.searchSort.value
					].join("|")}`;
				}
			}
			endpoint += `&sort=${sort_value}&order=${searchParameters.searchSort.direction}`;
		}

		if (hasDateRange || dateFilter) {
			let date_from =
				dateFilter === "future"
					? new Date().toLocaleDateString("en-US")
					: searchParameters.dateFrom
					? searchParameters.dateFrom.toLocaleDateString("en-US")
					: null;
			let date_to =
				dateFilter === "past"
					? new Date().toLocaleDateString("en-US")
					: searchParameters.dateTo
					? searchParameters.dateTo.toLocaleDateString("en-US")
					: null;

			if (date_from) {
				endpoint += `&date_from=${date_from}`;
			}
			if (date_to) {
				endpoint += `&date_to=${date_to}`;
			}
		}

		if (dateRangeLimits) {
			endpoint += `&abs_max_date=${dateRangeLimits.max.toLocaleDateString()}&abs_min_date=${dateRangeLimits.min.toLocaleDateString()}`;
		}

		let response = await fetchAs({
			endpoint,
			dispatch: _dispatch,
			testing: true,
		});

		if (
			response &&
			response.data &&
			response.data.results &&
			Array.isArray(response.data.results) &&
			response.data.results.length > 0
		) {
			let _excerpts = response.data.results.map((item) => {
				let _img;
				if (
					item.image &&
					typeof item.image.raw === "string" &&
					item.image.raw.length > 0
				) {
					try {
						_img = JSON.parse(item.image.raw);
					} catch (error) {
						console.error(error);
					}
				}
				let _vid;
				if (
					item.video &&
					typeof item.video.raw === "string" &&
					item.video.raw.length > 0
				) {
					try {
						_vid = JSON.parse(item.video.raw);
					} catch (error) {
						console.error(error);
					}
				}

				let light_e = {
					id: item.id.raw,
					type: item.type.raw,
					title: item.title.raw,
					summary: item.field_summary?.raw
						? item.field_summary.raw
						: item.field_summary?.snippet,
					subtitle: item.field_subtitle?.raw,
					slug: item.slug?.raw,
					image: _img,
					video: _vid,
					isHtmlSummary: true,
				};

				if (searchParameters.searchQuery) {
					light_e.hasSearchQuery = true;
					const fieldsWithText = [
						"title",
						"field_summary",
						"body",
						"field_section_main",
						"field_section_closing",
						"files",
						"field_section_sidebar",
						"field_subtitle",
					];

					for (const a_field_with_text of fieldsWithText) {
						if (item[a_field_with_text]) {
							const field_key = a_field_with_text.includes("field_")
								? a_field_with_text.substring(6)
								: a_field_with_text;
							light_e[field_key] = item[a_field_with_text].snippet
								? item[a_field_with_text].snippet
								: item[a_field_with_text].raw;
						}
					}

					let best_summary_field = [null, 0];

					for (const a_field_with_text of fieldsWithText) {
						const field_key = a_field_with_text.includes("field_")
							? a_field_with_text.substring(6)
							: a_field_with_text;
						if (field_key !== "title" && light_e[field_key]) {
							let snippet = light_e[field_key];
							let matches = snippet.match(/<em>/g);
							if (matches && matches.length > 0) {
								if (matches.length > best_summary_field[1]) {
									best_summary_field = [field_key, matches.length];
								}
							}
						}
					}
					if (!best_summary_field[0]) {
						best_summary_field = ["summary", 0];
					}
					light_e.summary = light_e[best_summary_field[0]];
					light_e.subtitle = null;
				}

				if (item.field_product_new_badge?.raw) {
					light_e["newBadge"] = true;
				}

				const { configs = {} } = pageData;
				let config = configs[item.type.raw] ? configs[item.type.raw] : null;
				if (config) {
					if (config.dateFormat) {
						light_e.dateFormat = config.dateFormat;
					}

					if (typeof config?.titleAppend == "string") {
						const fieldAppend = config.titleAppend.trim();
						if (item[fieldAppend]?.raw) {
							light_e["title_append"] = item[fieldAppend].raw;
						}
					}

					if (!!config?.options?.list?.attributes) {
						const { attributes } = config.options.list;
						Object.keys(attributes).forEach((attr) => {
							if (item[attr]?.raw) {
								if (Array.isArray(item[attr].raw)) {
									light_e[attr] = item[attr].raw.join(", ");
								} else {
									light_e[attr] = item[attr].raw;
								}
							}
						});
						light_e["extra_attributes"] = attributes;
					}
				}

				let dates;
				if (item.field_start_date && item.field_start_date.raw) {
					dates = [item.field_start_date.raw];
					if (item.field_end_date && item.field_end_date.raw) {
						dates.push(item.field_end_date.raw);
					} else {
						dates.push(item.field_start_date.raw);
					}
				} else if (item.created && item.created.raw) {
					dates = [item.created.raw];
				}
				if (dates) light_e.dates = dates;

				if (
					response.data.facet_names &&
					Array.isArray(response.data.facet_names)
				) {
					let hide_tags = config?.options?.list?.tagsHide;
					light_e.tags = response.data.facet_names
						.filter((field_name) => {
							let my_bundle = field_name.replace("field_", "");
							return (
								!hide_tags || (hide_tags && !hide_tags.includes(my_bundle))
							);
						})
						.map((field_name) => {
							let typeFound = typeDefinitions.find(
								(item) => `field_${item.bundle}` === field_name
							);
							if (typeFound) {
								let dataFound =
									typeof item[field_name] !== "undefined"
										? item[field_name].raw
										: null;
								if (dataFound) {
									dataFound = Array.isArray(dataFound)
										? dataFound
										: [dataFound];
									dataFound = dataFound.map((tag_name) => ({
										slug: `${searchPath}?filter=${field_name}&${field_name}=${encodeURIComponent(
											tag_name
										)}`,
										title: tag_name,
									}));
									return {
										label: typeFound.name,
										items: dataFound,
									};
								}
							}
							return null;
						})
						.filter((f) => f !== null);

					if (item.field_start_date && item.field_start_date.raw) {
						let start_date = getDateTimeFromValue(item.field_start_date.raw);
						light_e.field_start_date = start_date.toFormat("D");
					}
				}
				return light_e;
			});
			setItems(_excerpts);
			if (!showAllTermFilters) {
				setFiltersData(getElasticFacets(response.data, typeDefinitions));
			}
			setSearchPages(response.data.meta.page);
		} else {
			setItems([]);
			if (!showAllTermFilters) {
				setFiltersData([]);
			}
			setSearchPages(null);
		}

		setLoadingItems(false);
	};

	if (items === null)
		return (
			<Box style={{ textAlign: "center" }}>
				<CircularProgress />
			</Box>
		);
	if (items === false)
		return (
			<Box>
				<Alert severity="info">No items in this list</Alert>
			</Box>
		);

	return (
		<Filters
			id={id}
			data={filtersData}
			show={showFilters}
			onChangeQuery={onChangeQuery}
			onChangeFilter={onChangeFilter}
			onChangeDateTo={onChangeDateTo}
			onChangeDateFrom={onChangeDateFrom}
			onChangeSort={onChangeSort}
			layout={layout}
			typeDefinitions={typeDefinitions}
			onClearSearch={onClearSearch}
			onClearFilters={onClearFilters}
			loadingItems={loadingItems}
			sortOptions={_sortOptions}
			filtersTitle={filtersTitle}
			fullFacetsData={fullFacetsData}
			filterByType={
				(!types || (types && types.length === 0)) && !hideFilterByType
			}
			scrollToTop={scrollToTop}
			hiddenFacets={hiddenFacets}
			hasDateRange={hasDateRange}
			searchParameters={searchParameters}
			setSearchParameters={setSearchParameters}
			dateRangeLimits={dateRangeLimits}
			dateFilter={dateFilter}
			defaultSearchParameters={defaultSearchParameters}
			hasFilters={
				JSON.stringify(searchParameters.searchFilters) !==
				JSON.stringify(defaultSearchParameters.searchFilters)
			}
		>
			{items && items.length > 0 && mode === "calendar" && (
				<CalendarToggle
					calendarmode={calendarmode}
					setCalendarmode={setCalendarmode}
				/>
			)}
			{loadingItems ? (
				<Loading />
			) : items.length === 0 ? (
				<NoResults onResetSearch={onResetSearch} preloaded={preloaded} />
			) : (
				<Grid
					container
					spacing={isSidebar || mode === "list" ? 0 : 3}
					className={`entities-list entities-list-${mode} ${
						isSidebar ? "spacing-none" : "spacing"
					}`}
					sx={sx}
				>
					{mode == "table" ? (
						<ListTable
							rows={items}
							columns={field_table_columns}
							configs={configs}
						/>
					) : mode === "calendar" && calendarmode !== false ? (
						<CalendarGrid items={items} mode={calendarmode} />
					) : (
						<>
							{items.map((item) => (
								<Grid item key={item.id} md={mode === "list" ? 12 : 12 / cols}>
									<Excerpt
										{...item}
										isSidebar={isSidebar}
										mode={calendarmode === "list" ? "list" : mode}
										{...excerptAttrs}
										newBadge={item.newBadge}
										categories={item.tags}
									/>
								</Grid>
							))}
							{moreLink && (
								<Grid item md={12}>
									<More link={moreLink} />
								</Grid>
							)}
						</>
					)}
					<Pager
						pageSize={searchParameters.pageSize}
						handlePageSizeChange={handlePageSizeChange}
						onChangePage={onChangePage}
						searchPages={searchPages}
						localPagination={searchParameters.localPagination}
						defaultLimit={props.limit}
					/>
				</Grid>
			)}
		</Filters>
	);
}

function More({ link }) {
	const link_data = link ? extractLinkData(link, null, "More") : null;
	if (link_data) {
		return <Buttons links={[link_data]} type="link" />;
	} else {
		return <></>;
	}
}

function Sorting({ value, onChange, sortOptions }) {
	const sortDirection = value?.direction || "desc";
	value = value?.value || "_score";

	return (
		<Box sx={{ display: "flex", alignItems: "center" }}>
			<TextField
				id="outlined-select-currency-native"
				select
				label="Sort by"
				fullWidth
				SelectProps={{
					native: true,
				}}
				value={value}
				onChange={(e) =>
					onChange({ value: e.target.value, direction: sortDirection })
				}
				variant="filled"
			>
				{sortOptions.map((option) => (
					<option key={option.name} value={option.value}>
						{option.name}
					</option>
				))}
			</TextField>

			<Box
				component="button"
				value="check"
				selected={value.direction}
				onClick={() => {
					let newDirection;
					if (sortDirection == "asc") newDirection = "desc";
					else if (sortDirection == "desc") newDirection = "asc";
					onChange({ value: value, direction: newDirection });
				}}
				title={`Change sort direction to ${
					sortDirection == "asc" ? "descending" : "ascending"
				}`}
				sx={(theme) => ({
					padding: theme.spacing(1, 0, 1, 1),
					height: "100%",
					border: "none",
					backgroundColor: "transparent",
					cursor: "pointer",
					color: theme.palette.primary.main,
					height: "100%",
					"&:hover, &:active": {
						color: theme.palette.secondary.main,
					},
				})}
			>
				{sortDirection == "asc" ? <NorthIcon /> : <SouthIcon />}
			</Box>
		</Box>
	);
}

function Filter({
	id,
	title,
	items,
	last,
	searchFilters,
	onChangeFilter,
	multi = true,
}) {
	const ops = items.map((item) => ({ label: item.title, value: item.value }));
	const val = searchFilters[id] || [];
	if (multi) {
		return (
			<Autocomplete
				multiple
				id={id}
				options={ops}
				getOptionLabel={(option) => {
					if (typeof option === "string") {
						return option;
					} else if (option && option.value) {
						let op = ops.find((o) => {
							return o.value == option.value;
						});
						return op ? op.label : option;
					}
				}}
				isOptionEqualToValue={(option, value) => {
					return option.value === value.value;
				}}
				sx={(theme) => ({ marginBottom: last ? 0 : theme.spacing(2) })}
				onChange={(e, value) => {
					onChangeFilter(e, value, id);
				}}
				value={val}
				renderOption={(props, option, status) => {
					const { selected } = status;
					return (
						<li {...props}>
							<Checkbox
								icon={icon}
								checkedIcon={checkedIcon}
								style={{ marginRight: 8 }}
								checked={selected}
							/>
							{option.label}
						</li>
					);
				}}
				renderInput={(params) => (
					<TextField {...params} label={title} variant="filled" />
				)}
			/>
		);
	} else {
		return (
			<TextField
				id={id}
				select
				label={title}
				focused={val.length > 0}
				SelectProps={{
					native: true,
				}}
				value={val.length > 0 ? val[0].value : null}
				variant="filled"
				sx={(theme) => ({
					marginBottom: last ? 0 : theme.spacing(2),
					width: "100%",
				})}
				onChange={(e) => {
					if (e.target.value) {
						const sel_op = ops.find((op) => op.value == e.target.value);
						onChangeFilter(e, [sel_op], id);
					} else {
						onChangeFilter(e, [], id);
					}
				}}
			>
				<option value="" selected={!val || val.length === 0}></option>
				{ops.map((option) => {
					let my_val = val && val.length > 0 ? val[0].value : null;
					return (
						<option
							key={option.value}
							value={option.value}
							selected={my_val === option.value}
						>
							{option.label}
						</option>
					);
				})}
			</TextField>
		);
	}
}

function Pager({
	searchPages,
	pageSize,
	handlePageSizeChange,
	onChangePage,
	localPagination,
	defaultLimit,
}) {
	let _defaultLimit =
		defaultLimit && [10, 20, 30, 50].includes(parseInt(defaultLimit))
			? null
			: !!defaultLimit
			? parseInt(defaultLimit)
			: null;
	return searchPages && searchPages.total_pages > 1 ? (
		<Box sx={{ display: "block", width: "100%" }}>
			<Box
				spacing={4}
				display="flex"
				alignItems="center"
				sx={(theme) => ({ marginTop: theme.spacing(3) })}
			>
				<Pagination
					page={localPagination}
					count={searchPages.total_pages}
					onChange={onChangePage}
				/>
				<FormControl>
					<NativeSelect
						variant="filled"
						value={pageSize}
						onChange={handlePageSizeChange}
					>
						{_defaultLimit && (
							<option value={_defaultLimit}>{_defaultLimit}</option>
						)}
						<option value={10}>10</option>
						<option value={20}>20</option>
						<option value={30}>30</option>
						<option value={50}>50</option>
					</NativeSelect>
				</FormControl>
			</Box>
		</Box>
	) : null;
}

function Filters({
	children,
	id,
	data,
	show = false,
	onChangeQuery,
	onChangeFilter,
	onChangeDateTo,
	onChangeDateFrom,
	layout,
	onChangeSort,
	typeDefinitions,
	onClearSearch,
	onClearFilters,
	loadingItems,
	sortOptions,
	filtersTitle,
	fullFacetsData,
	filterByType,
	hiddenFacets,
	hasDateRange,
	searchParameters,
	setSearchParameters,
	dateRangeLimits,
	dateFilter,
	multi = true,
	defaultSearchParameters,
	hasFilters,
}) {
	if (!show) return children;	
	
	const {
		configs,
		themeConfig
	} = useContext(SiteContext);

	const typeName = themeConfig?.accessibilityOptions?.typeTitle || "Type";

	const fromMaxDate = searchParameters.dateTo
		? addDays(searchParameters.dateTo, -1)
		: dateFilter === "past"
		? new Date()
		: dateRangeLimits
		? addDays(dateRangeLimits.max, -1)
		: null;

	const toMinDate = searchParameters.dateFrom
		? addDays(searchParameters.dateFrom, 1)
		: dateFilter === "future"
		? new Date()
		: dateRangeLimits
		? addDays(dateRangeLimits.min, 1)
		: null;

	const visibleFilters = data.filter((fl) => !hiddenFacets.includes(fl.id));

	const hasFilterByType =
		filterByType &&
		fullFacetsData &&
		typeof fullFacetsData.type !== "undefined" &&
		fullFacetsData.type.length > 0;

	// Custom name for content types ON filter by type
	const typesCustomNames = Object.keys(configs)
		.filter((key) => 'searchName' in configs[key])
		.reduce((obj, key) => {
		  obj[key] = configs[key];
		  return obj;
		}, {});

	if (fullFacetsData && fullFacetsData.type) {
		fullFacetsData?.type.forEach((typeObj) => {
			const matchingCustomName = typesCustomNames[typeObj.value];
			if (matchingCustomName) {
			  typeObj.title = matchingCustomName.searchName;
			}
		  });
	}

	const showAllTermFilters = 
		themeConfig?.globalSearch?.showAllTermFilters || false;
	
	const hasSidebar =
		layout === "sidebar" &&
		(visibleFilters.length > 0 || hasFilterByType || hasDateRange);

	let titles = {
		type: typeName,
	};

	if (typeDefinitions) {
		data.forEach((filter) => {
			let typeFound = typeDefinitions.find(
				(item) => `field_${item.bundle}` == filter.id
			);
			if (typeFound) {
				titles[filter.title] = typeFound.name;
			}
		});
	}
	let QueryInputProps = {};
	if (searchParameters.searchQuery && !loadingItems) {
		QueryInputProps = {
			endAdornment: (
				<InputAdornment position="end">
					<IconButton
						aria-label="Clear search"
						edge="end"
						onClick={onClearSearch}
						disabled={!searchParameters.searchQuery}
					>
						<ClearIcon />
					</IconButton>
				</InputAdornment>
			),
		};
	}
	let ResetFilterInputProps = {};
	if (!searchParameters.searchQuery && !loadingItems) {
		ResetFilterInputProps = {
			endAdornment: (
				<InputAdornment position="end">
					<IconButton
						aria-label="Clear filters"
						edge="end"
						onClick={onClearFilters}
					>
						<ClearIcon />
					</IconButton>
				</InputAdornment>
			),
		};
	}

	return (
		<Grid
			container
			spacing={4}
			sx={{
				"& .MuiFilledInput-root": {
					background: "rgb(255, 255, 255)",
					borderRadius: "4px",
					boxShadow: "0px 0px 0px 1px rgba(0, 0, 0, 0.1)",
					"&:before, &:after": {
						display: "none",
					},
				},
			}}
		>
			{hasSidebar && (
				<Grid item sm={3}>
					<FilterPaper>
						{filtersTitle ? (
							<Typography
								sx={(theme) => ({
									marginBottom: theme.spacing(1),
									fontWeight: theme.typography.fontWeightNormal,
									textAlign: "left",
									fontSize: "0.86em",
									opacity: 0.6,
								})}
								component="h2"
							>
								{filtersTitle}
							</Typography>
						) : null}

						{hasDateRange && dateRangeLimits && (
							<>
								{dateFilter !== "future" && (
									<Box sx={(theme) => ({ marginBottom: theme.spacing(2) })}>
										<DatePicker
											placeholderText="From"
											maxDate={fromMaxDate}
											minDate={dateRangeLimits.min}
											dateFormat="MM/dd/yyyy"
											showYearDropdown
											selected={
												searchParameters.dateFrom
													? searchParameters.dateFrom
													: null
											}
											onChange={onChangeDateFrom}
										/>
									</Box>
								)}
								{dateFilter !== "past" && (
									<Box sx={(theme) => ({ marginBottom: theme.spacing(2) })}>
										<DatePicker
											placeholderText="To"
											maxDate={dateRangeLimits.max}
											minDate={toMinDate}
											selected={
												searchParameters.dateTo ? searchParameters.dateTo : null
											}
											dateFormat="MM/dd/yyyy"
											showYearDropdown
											onChange={onChangeDateTo}
										/>
									</Box>
								)}
								{(searchParameters.dateTo || searchParameters.dateFrom) && (
									<Typography
										variant="caption"
										component="a"
										href="#"
										sx={(theme) => ({
											marginBottom: theme.spacing(2),
											color: theme.palette.link.main,
											display: "block",
											marginTop: theme.spacing(-1.5),
											textAlign: "right",
										})}
										onClick={(e) => {
											e.preventDefault();
											let _searchParameters = {
												...searchParameters,
												dateTo: null,
												dateFrom: null,
											};
											setSearchParameters(_searchParameters);
										}}
									>
										Clear dates
									</Typography>
								)}
							</>
						)}
						{hasFilterByType && !showAllTermFilters && (
							<Filter
								id="type"
								title={titles["type"]}
								items={fullFacetsData.type}
								searchFilters={searchParameters.searchFilters}
								onChangeFilter={onChangeFilter}
								last={visibleFilters.length === 0}
							/>
						)}
						{visibleFilters.map((filter, i) => (
							<Filter
								key={filter.id}
								{...filter}
								title={titles[filter.title] || filter.title}
								searchFilters={searchParameters.searchFilters}
								onChangeFilter={onChangeFilter}
								last={i === visibleFilters.length - 1}
								multi={multi}
							/>
						))}
						{!multi && hasFilters && (
							<Typography
								variant="caption"
								component="a"
								href="#"
								sx={(theme) => ({
									color: theme.palette.link.main,
									display: "block",
									marginTop: theme.spacing(1.5),
									textAlign: "right",
								})}
								onClick={(e) => {
									e.preventDefault();
									setSearchParameters(defaultSearchParameters);
								}}
							>
								Clear filters
							</Typography>
						)}
					</FilterPaper>
				</Grid>
			)}
			<Grid item sm={hasSidebar ? 9 : 12}>
				<FilterPaper
					layout={layout}
					sx={(theme) => ({ marginBottom: theme.spacing(3) })}
				>
					<Grid container spacing={2}>
						<Grid item sm={9}>
							<TextField
								id={`search-query-${id}`}
								label="Search..."
								fullWidth
								variant="filled"
								onChange={onChangeQuery}
								value={searchParameters.searchQuery}
								InputProps={QueryInputProps}
							/>
						</Grid>
						<Grid item sm={3}>
							<Sorting
								value={searchParameters.searchSort}
								onChange={onChangeSort}
								sortOptions={sortOptions}
							/>
						</Grid>
					</Grid>
				</FilterPaper>
				{children}
			</Grid>
		</Grid>
	);
}

function Loading() {
	return (
		<Box style={{ textAlign: "center" }}>
			<CircularProgress />
		</Box>
	);
}

function NoResults({ onResetSearch, preloaded }) {
	return (
		<Alert severity="info" sx={{ alignItems: "center" }}>
			No results{" "}
			{!preloaded && (
				<Button
					variant="contained"
					size="small"
					sx={(theme) => ({ marginLeft: theme.spacing(1.5) })}
					onClick={onResetSearch}
				>
					Clear search
				</Button>
			)}
		</Alert>
	);
}

export default ListComponent;
